import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Stack, Group, Text, Badge, Button, TextInput, Divider, ScrollArea } from '@mantine/core';
import { IconPencil, IconCheck, IconRefresh, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { OptimizationService } from '../../services/optimization/OptimizationService';
import { OPTIMIZATION_STATUS } from '../../services/optimization/constants';
import { toast } from '../../ui/toast';

function formatDefaultName(ts) {
  const d = ts instanceof Date ? ts : (ts?.toDate ? ts.toDate() : null) || new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `Run ${dd}-${mm}-${yyyy} ${hh}:${min}`;
}

function statusBadgeColor(s) {
  if (!s) return 'gray';
  const u = String(s).toUpperCase();
  if (u === OPTIMIZATION_STATUS.SUCCEEDED) return 'green';
  if (u === OPTIMIZATION_STATUS.FAILED) return 'red';
  if (u === OPTIMIZATION_STATUS.RUNNING) return 'blue';
  if (u === OPTIMIZATION_STATUS.QUEUED) return 'yellow';
  return 'gray';
}

export default function OptimizationHistoryModal({
  opened,
  onClose,
  planningId,
  // When the user wants to deep review a run, we'll surface it to parent
  onReviewRun, // async (run, result) => void
  onReapplied, // async () => void
}) {
  const { t } = useTranslation('common');
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [reapplyingId, setReapplyingId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);

  const loadRuns = useCallback(async () => {
    if (!planningId) return;
    setLoading(true);
    try {
      const list = await OptimizationService.listRuns(planningId);
      setRuns(list);
    } finally {
      setLoading(false);
    }
  }, [planningId]);

  useEffect(() => {
    if (opened) loadRuns();
  }, [opened, loadRuns]);

  const startEdit = (run) => {
    setEditingId(run.id);
    setEditValue(run.name || formatDefaultName(run.createdAt));
  };

  const saveEdit = async (run) => {
    const name = (editValue || '').trim() || null;
    await OptimizationService.updateRunName(planningId, run.id, name);
    setEditingId(null);
    setEditValue('');
    await loadRuns();
  };

  const doReapply = async (run) => {
    setReapplyingId(run.id);
    try {
      const result = await OptimizationService.fetchRunResult(planningId, run);
      if (!result) throw new Error('No result available for this run');
      await OptimizationService.applyAssignmentsFromResult(planningId, run, result);
      toast.success(t('opt_results.applied_title', { defaultValue: 'Assignments applied' }), {
        message: t('opt_results.applied_msg', { defaultValue: 'Optimization assignments have been applied' })
      });
      await onReapplied?.();
    } catch (e) {
      toast.error(String(e?.message || e || 'Re-apply failed'));
    } finally {
      setReapplyingId(null);
    }
  };

  const doReview = async (run) => {
    setReviewingId(run.id);
    try {
      const result = await OptimizationService.fetchRunResult(planningId, run);
      if (!result) {
        toast.warn(t('opt_results.no_changes_title', { defaultValue: 'No changes' }), {
          message: t('opt_results.no_changes_msg', { defaultValue: 'Nothing to apply from results' })
        });
        return;
      }
      await onReviewRun?.(run, result);
      // Close history to display the results modal clearly
      onClose?.();
    } finally {
      setReviewingId(null);
    }
  };

  const header = (
    <Group justify="space-between" align="center">
      <Text fw={600}>{t('opt_history.title', { defaultValue: 'Optimization History' })}</Text>
      <Button variant="subtle" leftSection={<IconRefresh size={16} />} onClick={loadRuns} loading={loading}>
        {t('opt_history.refresh', { defaultValue: 'Refresh' })}
      </Button>
    </Group>
  );

  return (
    <Modal opened={opened} onClose={onClose} title={header} size="xl">
      <Stack gap="sm">
        {runs.length === 0 ? (
          <Text c="dimmed" size="sm">{t('opt_history.none', { defaultValue: 'No optimization runs yet.' })}</Text>
        ) : (
          <ScrollArea h={420} offsetScrollbars>
            <Stack gap="xs">
              {runs.map((run) => {
                const status = run.status || null;
                const created = run.createdAt?.toDate ? run.createdAt.toDate() : (run.createdAt || null);
                const display = run.name || formatDefaultName(created);
                return (
                  <div key={run.id} style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8, padding: 12 }}>
                    <Group justify="space-between" align="center">
                      <Stack gap={4} style={{ flex: 1 }}>
                        {editingId === run.id ? (
                          <Group gap={8} wrap="nowrap">
                            <TextInput
                              style={{ flex: 1 }}
                              value={editValue}
                              onChange={(e) => setEditValue(e.currentTarget.value)}
                              placeholder={t('opt_history.name_placeholder', { defaultValue: 'Run name' })}
                            />
                            <Button size="xs" leftSection={<IconCheck size={14} />} onClick={() => saveEdit(run)}>
                              {t('common.save', { defaultValue: 'Save' })}
                            </Button>
                          </Group>
                        ) : (
                          <Group gap={8}>
                            <Text fw={600}>{display}</Text>
                            <Button size="xs" variant="subtle" leftSection={<IconPencil size={14} />} onClick={() => startEdit(run)}>
                              {t('common.edit', { defaultValue: 'Edit' })}
                            </Button>
                          </Group>
                        )}
                        <Group gap={8}>
                          {created && <Text size="xs" c="dimmed">{created.toLocaleString()}</Text>}
                          <Badge color={statusBadgeColor(status)} variant="light">{status || '—'}</Badge>
                        </Group>
                      </Stack>
                      <Group gap={8}>
                        <Button
                          size="xs"
                          variant="default"
                          leftSection={<IconSearch size={14} />}
                          loading={reviewingId === run.id}
                          onClick={() => doReview(run)}
                        >
                          {t('opt_history.review', { defaultValue: 'Review' })}
                        </Button>
                        <Button
                          size="xs"
                          onClick={() => doReapply(run)}
                          loading={reapplyingId === run.id}
                        >
                          {t('opt_history.reapply', { defaultValue: 'Re-apply' })}
                        </Button>
                      </Group>
                    </Group>
                  </div>
                );
              })}
            </Stack>
          </ScrollArea>
        )}
        <Divider my={4} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
